/** 従業員の登録・更新フォーム(登録と詳細の編集で同じものを使う)。 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Button,
  Checkbox,
  DatePicker,
  Drawer,
  Form,
  Input,
  Modal,
  Select,
  Space,
  TreeSelect,
  message,
} from "antd";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";

import type { CustomField, EmployeeDetail, EmployeeInput } from "../../lib/api";
import { useApi } from "../../lib/useApi";
import { EMPLOYEE_STATUSES, EMPLOYMENT_TYPES } from "./constants";
import {
  errorText,
  positionOptions,
  unitOptions,
  useCustomFields,
  useOrgUnits,
  usePositions,
} from "./hooks";
import { SimilarWarning } from "./SimilarWarning";

type FormValues = {
  emp_code: string;
  family_name: string;
  given_name: string;
  family_name_kana?: string;
  given_name_kana?: string;
  email?: string;
  employment_type?: string;
  work_mobile?: string;
  work_phone?: string;
  extension?: string;
  status?: string;
  org_unit_id?: number | null;
  position_id?: number | null;
  manager_id?: number | null;
  note?: string;
  joined_at?: dayjs.Dayjs | null;
  left_at?: dayjs.Dayjs | null;
  /** カスタム項目(key ごと。日付は Dayjs) */
  extra?: Record<string, string | dayjs.Dayjs | null | undefined>;
};

/** カスタム項目の値を送る形(文字列)に直す。空は "" で送る(未設定に戻す) */
function extraOut(fields: CustomField[], v: FormValues["extra"]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const f of fields) {
    const raw = v?.[f.key];
    if (raw == null) out[f.key] = "";
    else if (typeof raw === "string") out[f.key] = raw;
    else out[f.key] = raw.format("YYYY-MM-DD");
  }
  return out;
}

// 電話の形(サーバーの `phone_value` と同じ。全角はサーバーが半角に寄せる)
const PHONE_RULES = [
  { pattern: /^[0-9０-９+＋\-－ー()（） \u3000]*$/, message: "数字と + - ( ) 空白だけで書く" },
];

/** 上長の候補。氏名・社員番号で引けるようにサーバー側の検索をそのまま使う。
 *  新しい申請 › 入社申請の対象者段(従業員の新規入力)でも使う。 */
export function ManagerSelect({
  id,
  value,
  selectedLabel,
  onChange,
}: {
  id?: string;
  value?: number | null;
  /** 入社申請で段を戻ったときの表示用。従業員フォームでは候補だけを使う */
  selectedLabel?: string;
  onChange?: (v: number | null, label?: string) => void;
}) {
  const api = useApi();
  const [options, setOptions] = useState<{ value: number; label: string }[]>([]);
  const [loading, setLoading] = useState(false);
  // 対象者段が再表示されて候補がまだ無いときも、選択済みの氏名・社員番号を復元する
  const selectOptions = value != null && selectedLabel != null
    ? [{ value, label: selectedLabel }, ...options.filter((o) => o.value !== value)]
    : options;

  const search = async (q: string) => {
    setLoading(true);
    try {
      const res = await api.employees(
        new URLSearchParams({ q, limit: "20", status: "active" }),
      );
      setOptions(
        res.items.map((e) => ({
          value: e.id,
          label: `${e.name}(${e.emp_code}${e.position_name ? " / " + e.position_name : ""})`,
        })),
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Select
      id={id}
      allowClear
      showSearch
      filterOption={false}
      placeholder="氏名・社員番号で検索(未指定なら役職から自動導出)"
      value={value ?? undefined}
      loading={loading}
      options={selectOptions}
      onSearch={(q) => void search(q)}
      onFocus={() => {
        if (options.length === 0) void search("");
      }}
      onChange={(v) => onChange?.(v ?? null, v == null ? undefined : selectOptions.find((o) => o.value === v)?.label)}
    />
  );
}

export function EmployeeFormModal({
  open,
  employee,
  onClose,
  as = "modal",
}: {
  open: boolean;
  /** 未指定なら新規登録 */
  employee?: EmployeeDetail;
  onClose: () => void;
  /** 詳細ページでは Drawer(一覧の登録は Modal のまま) */
  as?: "modal" | "drawer";
}) {
  const api = useApi();
  const qc = useQueryClient();
  const [form] = Form.useForm<FormValues>();
  const units = useOrgUnits();
  const positions = usePositions();
  const customFields = useCustomFields();
  const fields = customFields.data?.items ?? [];
  // 新規登録のときだけ、同じ人の二重登録に気づけるよう入力中の氏名・メールを見る
  const wFamily = (Form.useWatch("family_name", form) as string | undefined) ?? "";
  const wGiven = (Form.useWatch("given_name", form) as string | undefined) ?? "";
  const wEmail = (Form.useWatch("email", form) as string | undefined) ?? "";

  // 移籍(協力会社 → 正社員など): 契約形態を変えて接頭辞が合わなくなるとき、社員番号を
  // 採番し直すかを聞く。判定はサーバー(採番規則は 1 か所に置く)
  const etWatch = Form.useWatch("employment_type", form) as string | undefined;
  const etChanged = !!employee && !!etWatch && etWatch !== employee.employment_type;
  const [renumber, setRenumber] = useState(true);
  const preview = useQuery({
    queryKey: ["hr", "renumber-preview", employee?.id, etWatch],
    queryFn: () => api.renumberPreview(employee!.id, etWatch!),
    enabled: open && etChanged,
  });
  const showRenumber = etChanged && preview.data?.changes === true;
  const doRenumber = showRenumber && renumber;

  // 入れ直すのは**開いた瞬間と対象者が変わったとき**だけ。`employee` は一覧・詳細の
  // クエリの返りで作り直されるので、開いている間の再取得で入力途中を消さない
  const formKey = useRef<string>("");
  useEffect(() => {
    if (!open) {
      formKey.current = "";
      return;
    }
    const key = String(employee?.id ?? "new");
    if (formKey.current === key) {
      // 定義が後から届いたとき: 文字列のまま入っている日付だけ Dayjs に直す
      // (DatePicker に文字列を渡すと落ちる)。入力途中の他の欄は触らない
      for (const f of fields) {
        if (f.type !== "date") continue;
        const cur: unknown = form.getFieldValue(["extra", f.key]);
        if (typeof cur === "string" && cur) form.setFieldValue(["extra", f.key], dayjs(cur));
      }
      return;
    }
    formKey.current = key;
    setRenumber(true);
    form.resetFields();
    if (employee)
      form.setFieldsValue({
        emp_code: employee.emp_code,
        family_name: employee.family_name,
        given_name: employee.given_name,
        family_name_kana: employee.family_name_kana,
        given_name_kana: employee.given_name_kana,
        email: employee.email,
        employment_type: employee.employment_type || undefined,
        work_mobile: employee.work_mobile,
        work_phone: employee.work_phone,
        extension: employee.extension,
        status: employee.status,
        org_unit_id: employee.org_unit_id,
        position_id: employee.position_id,
        manager_id: employee.manager_id,
        note: employee.note,
        joined_at: employee.joined_at ? dayjs(employee.joined_at) : null,
        left_at: employee.left_at ? dayjs(employee.left_at) : null,
        extra: Object.fromEntries(
          Object.entries(employee.extra ?? {}).map(([k, val]) => [
            k,
            fields.find((f) => f.key === k)?.type === "date" && val ? dayjs(val) : val,
          ]),
        ),
      });
    // fields は定義の取得で後から届くことがある。届いたら日付だけ Dayjs に直す
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, employee, form, customFields.data]);

  const save = useMutation({
    mutationFn: (body: EmployeeInput) =>
      employee ? api.updateEmployee(employee.id, body) : api.createEmployee(body),
    onSuccess: (row) => {
      void message.success(employee ? "更新した" : `登録した(${row.name})`);
      void qc.invalidateQueries({ queryKey: ["hr"] });
      void qc.invalidateQueries({ queryKey: ["audit"] });
      onClose();
    },
    onError: (e) => void message.error(errorText(e)),
  });

  const submit = async () => {
    const v = await form.validateFields();
    // 未選択(undefined / null)は「解除」の 0 で送る。部分更新でも同じ意味になる
    save.mutate({
      // 新規で空なら送らない = サーバーが契約形態から採番する。
      // 移籍で採番し直すときは番号を送らず renumber_emp_code で頼む(同時には送れない)
      ...(doRenumber ? { renumber_emp_code: true } : { emp_code: v.emp_code ?? "" }),
      family_name: v.family_name,
      given_name: v.given_name,
      family_name_kana: v.family_name_kana ?? "",
      given_name_kana: v.given_name_kana ?? "",
      email: v.email ?? "",
      employment_type: v.employment_type ?? "",
      work_mobile: v.work_mobile ?? "",
      work_phone: v.work_phone ?? "",
      extension: v.extension ?? "",
      status: v.status ?? "pre_onboard",
      org_unit_id: v.org_unit_id ?? 0,
      position_id: v.position_id ?? 0,
      manager_id: v.manager_id ?? 0,
      note: v.note ?? "",
      joined_at: v.joined_at ? v.joined_at.format("YYYY-MM-DD") : null,
      left_at: v.left_at ? v.left_at.format("YYYY-MM-DD") : null,
      extra: extraOut(fields, v.extra),
    });
  };

  const title = employee ? `従業員の編集(${employee.name})` : "従業員の登録";
  const body = (
      <Form form={form} layout="vertical" style={{ marginTop: as === "modal" ? 16 : 0 }}>
        <Form.Item
          name="emp_code"
          label="社員番号"
          rules={employee ? [{ required: true, message: "社員番号は必須" }] : []}
          extra={
            employee
              ? undefined
              : "空のまま保存すると契約形態から自動で採番する(正社員 AM / その他 OT / それ以外 BP + 次の番号)"
          }
        >
          <Input maxLength={40} placeholder={employee ? "AM101" : "空なら自動採番"} disabled={doRenumber} />
        </Form.Item>
        {/* 氏名は姓・名で入れる。表示名(`name`)はサーバーが導出するので入力欄は無い */}
        <Form.Item name="family_name" label="姓" rules={[{ required: true, message: "姓は必須" }]}>
          <Input maxLength={100} placeholder="山田" />
        </Form.Item>
        <Form.Item name="given_name" label="名" rules={[{ required: true, message: "名は必須" }]}>
          <Input maxLength={100} placeholder="太郎" />
        </Form.Item>
        <Form.Item name="family_name_kana" label="姓カナ">
          <Input maxLength={100} placeholder="ヤマダ(ひらがなはカタカナに直して保存する)" />
        </Form.Item>
        <Form.Item name="given_name_kana" label="名カナ">
          <Input maxLength={100} placeholder="タロウ" />
        </Form.Item>
        <Form.Item name="email" label="メールアドレス">
          <Input maxLength={200} placeholder="空でもよい(重複は大小文字を無視して弾く)" />
        </Form.Item>
        {!employee && open && (
          <SimilarWarning familyName={wFamily} givenName={wGiven} email={wEmail} onLeave={onClose} />
        )}
        <Form.Item
          name="employment_type"
          label="契約形態"
          rules={[{ required: true, message: "契約形態は必須" }]}
        >
          <Select options={EMPLOYMENT_TYPES.map((t) => ({ value: t, label: t }))} />
        </Form.Item>
        {showRenumber && preview.data && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 24 }}
            message={
              <Checkbox checked={renumber} onChange={(e) => setRenumber(e.target.checked)}>
                社員番号も採番し直す({preview.data.current} → {preview.data.next})
              </Checkbox>
            }
            description="契約形態が変わると社員番号の接頭辞も変わる(正社員 AM / その他 OT / それ以外 BP)。外すと今の番号のまま契約形態だけ変える。番号を変えても端末・申請・貸与の紐づけは切れない"
          />
        )}
        {/* 電話は任意。電話帳同期(ID管理)が会社電話帳として配る */}
        <Form.Item name="work_mobile" label="会社携帯" rules={PHONE_RULES}>
          <Input maxLength={40} placeholder="090-1234-5678" />
        </Form.Item>
        <Form.Item name="work_phone" label="会社電話" rules={PHONE_RULES}>
          <Input maxLength={40} placeholder="03-1234-5678" />
        </Form.Item>
        <Form.Item name="extension" label="内線" rules={PHONE_RULES}>
          <Input maxLength={40} placeholder="1234" />
        </Form.Item>
        <Form.Item name="status" label="在籍状態">
          <Select
            options={EMPLOYEE_STATUSES.map((s) => ({ value: s.value, label: s.label }))}
          />
        </Form.Item>
        <Form.Item name="org_unit_id" label="所属(主所属)">
          <TreeSelect
            allowClear
            treeDefaultExpandAll
            treeData={unitOptions(units.data?.items ?? [])}
            placeholder="未所属"
          />
        </Form.Item>
        <Form.Item name="position_id" label="役職">
          <Select allowClear options={positionOptions(positions.data?.items ?? [])} />
        </Form.Item>
        <Form.Item name="manager_id" label="上長(手動指定)">
          <ManagerSelect />
        </Form.Item>
        <Form.Item name="joined_at" label="入社日">
          <DatePicker style={{ width: "100%" }} />
        </Form.Item>
        <Form.Item name="left_at" label="退職日">
          <DatePicker style={{ width: "100%" }} />
        </Form.Item>
        <Form.Item name="note" label="備考">
          <Input.TextArea rows={2} maxLength={500} />
        </Form.Item>
        {/* カスタム項目(人事 › 設定 › カスタム項目 で定義。会社ごとに違う欄) */}
        {fields.map((f) => (
          <Form.Item
            key={f.key}
            name={["extra", f.key]}
            label={f.label}
            rules={f.required ? [{ required: true, message: `${f.label}は必須` }] : []}
          >
            {f.type === "select" ? (
              <Select allowClear options={f.options.map((o) => ({ value: o, label: o }))} />
            ) : f.type === "date" ? (
              <DatePicker style={{ width: "100%" }} />
            ) : (
              <Input maxLength={200} />
            )}
          </Form.Item>
        ))}
      </Form>
  );

  if (as === "drawer") {
    return (
      <Drawer
        open={open}
        title={title}
        width={560}
        onClose={onClose}
        destroyOnClose
        footer={
          <Space style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button onClick={onClose}>やめる</Button>
            <Button type="primary" loading={save.isPending} onClick={() => void submit()}>
              保存
            </Button>
          </Space>
        }
      >
        {body}
      </Drawer>
    );
  }
  return (
    <Modal
      open={open}
      title={title}
      okText="保存"
      cancelText="やめる"
      confirmLoading={save.isPending}
      onOk={() => void submit()}
      onCancel={onClose}
      width={640}
      destroyOnClose
    >
      {body}
    </Modal>
  );
}
